import { useEffect, useState, useRef } from 'react';
import { useSearchParams, useNavigate, useLocation } from 'react-router-dom';
import { Button, Spin, Alert, Tag, Typography, Divider } from 'antd';
import { UploadOutlined, CheckCircleFilled, FilePdfOutlined, FileImageOutlined } from '@ant-design/icons';
import { api } from '../../services/api';
import { useClient } from '../../context/ClientContext';
import { useCarrito } from '../../context/CarritoContext';

const { Title, Text } = Typography;

export const CheckoutTransferencia = () => {
  // Llega con el carrito, no con un pedido: el pedido nace al confirmar.
  const [searchParams] = useSearchParams();
  const carritoId = searchParams.get('carrito_id');
  const { state } = useLocation();
  const { clientId, theme , clientPath} = useClient();
  const { refreshCart } = useCarrito();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [datos, setDatos] = useState([]);
  const [total, setTotal] = useState(null);
  const [totalOriginal, setTotalOriginal] = useState(null);
  const [descuento, setDescuento] = useState(0);
  const [archivos, setArchivos] = useState([]);
  const [confirmando, setConfirmando] = useState(false);
  const [confirmado, setConfirmado] = useState(false);
  // A qué cuenta transfirió: con eso la tienda concilia el comprobante.
  const [cuentaId, setCuentaId] = useState(null);

  const inputRef = useRef(null);

  useEffect(() => {
    if (!carritoId || !state) {
      setError('No encontramos los datos del pago. Volvé al carrito y elegí el medio de pago otra vez.');
      setLoading(false);
      return;
    }
    setDatos(state.cuentas || []);
    // Con una sola cuenta no hay nada que elegir.
    if ((state.cuentas || []).length === 1) setCuentaId(state.cuentas[0].id);
    setTotal(state.total);
    setTotalOriginal(state.totalOriginal);
    setDescuento(state.descuento || 0);
    setLoading(false);
  }, [carritoId, state]);

  // Los comprobantes esperan en el navegador: no hay pedido al que adjuntarlos
  // todavia, y subirlos antes dejaria archivos huerfanos si se arrepiente.
  const handleFileChange = (e) => {
    const files = Array.from(e.target.files);
    if (!files.length) return;
    setArchivos(prev => [
      ...prev,
      ...files.map(file => ({ nombre: file.name, estado: 'ok', mime: file.type, file })),
    ]);
    e.target.value = '';
  };

  // Recien aca nace el pedido, y hereda la reserva que el carrito ya tenia.
  const handleConfirmar = async () => {
    if (!archivos.some(a => a.estado === 'ok')) {
      alert('Debés adjuntar al menos un comprobante antes de confirmar.');
      return;
    }
    // Antes de crear el pedido: si faltara la cuenta, el pedido ya quedaría
    // hecho y el comprobante subido sin poder confirmarse.
    if (datos.length > 0 && !cuentaId) {
      alert('Elegí a qué cuenta transferiste.');
      return;
    }
    setConfirmando(true);
    try {
      const pedidoRes = await api.ventas_pedidos.create(carritoId, { total_esperado: totalOriginal });
      if (pedidoRes.status !== 'success') {
        alert(pedidoRes.message || 'No se pudo generar el pedido');
        return;
      }
      const pedidoId = pedidoRes.data?.id_pedido;

      await api.pagos.iniciarTransferencia(pedidoId);

      for (const a of archivos) {
        if (!a.file) continue;
        const res = await api.pagos.subirAdjunto(pedidoId, a.file);
        if (res.status !== 'success') {
          alert(res.message || `No se pudo subir ${a.nombre}`);
          return;
        }
      }

      const res = await api.pagos.confirmarAdjunto(pedidoId, cuentaId ? { cuenta_id: cuentaId } : undefined);
      if (res.status !== 'success') {
        alert(res.message || 'Error al confirmar');
        return;
      }
      setConfirmado(true);
      await refreshCart();
      setTimeout(() => navigate(clientPath(`/pedido/pendiente?external_reference=${pedidoId}`)), 2500);
    } catch (err) {
      console.error(err);
      alert('Error al confirmar el pago');
    } finally {
      setConfirmando(false);
    }
  };

  if (loading) return <div style={{ padding: 40, textAlign: 'center' }}><Spin size="large" /></div>;

  if (error) return (
    <div style={{ padding: 40, textAlign: 'center' }}>
      <Alert type="error" message={error} style={{ marginBottom: 16 }} />
      <Button type="primary" onClick={() => navigate(clientPath('/carrito'))}>Volver al carrito</Button>
    </div>
  );

  if (confirmado) return (
    <div style={{ padding: 40, textAlign: 'center' }}>
      <CheckCircleFilled style={{ fontSize: 64, color: '#52c41a', marginBottom: 16 }} />
      <Title level={4}>¡Comprobante enviado!</Title>
      <Text type="secondary">Redirigiendo...</Text>
    </div>
  );

  return (
    <div style={{ maxWidth: 620, margin: '40px auto', padding: '0 20px' }}>
      <Title level={4} style={{ marginBottom: 24 }}>Pago por transferencia</Title>

      {/* Importe */}
      <div style={{ border: '1px solid #e0e0e0', borderRadius: 8, padding: 20, marginBottom: 20, background: '#fafafa' }}>
        {total != null && descuento !== 0 && <InfoRow label="Subtotal" value={`$ ${Number(totalOriginal).toFixed(2)}`} />}
        {descuento > 0 && <InfoRow label={`Descuento (${descuento}%)`} value={`- $ ${(totalOriginal - total).toFixed(2)}`} color="green" />}
        {descuento < 0 && <InfoRow label={`Recargo (${Math.abs(descuento)}%)`} value={`+ $ ${(total - totalOriginal).toFixed(2)}`} color="red" />}
        {total != null && <InfoRow label="Importe a transferir" value={`$ ${Number(total).toFixed(2)}`} bold />}
      </div>

      {/* Cuentas */}
      {datos.length > 0 ? (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12, marginBottom: 24 }}>
          <Text strong style={{ fontSize: 15 }}>
            {datos.length > 1 ? 'Transferí a una de estas cuentas y marcá a cuál:' : 'Transferí a esta cuenta:'}
          </Text>
          {datos.map((cuenta) => {
            const elegida = cuentaId === cuenta.id;
            const color = theme?.primaryColor || '#111827';
            return (
            <div key={cuenta.id}
              role={datos.length > 1 ? 'radio' : undefined}
              aria-checked={datos.length > 1 ? elegida : undefined}
              tabIndex={datos.length > 1 ? 0 : undefined}
              onClick={() => setCuentaId(cuenta.id)}
              onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setCuentaId(cuenta.id); } }}
              style={{
                border: `${elegida && datos.length > 1 ? 2 : 1}px solid ${elegida && datos.length > 1 ? color : '#e0e0e0'}`,
                borderRadius: 10, padding: elegida && datos.length > 1 ? 19 : 20, background: '#fff',
                cursor: datos.length > 1 ? 'pointer' : 'default',
              }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
                <Text strong style={{ fontSize: 14 }}>{cuenta.nombre}</Text>
                {datos.length > 1 && (elegida
                  ? <Tag color="success" icon={<CheckCircleFilled />}>Transferí acá</Tag>
                  : <Text type="secondary" style={{ fontSize: 12 }}>Tocá si transferiste acá</Text>)}
              </div>
              {cuenta.entidad && <InfoRow label="Entidad" value={cuenta.entidad} />}
              {cuenta.banco   && <InfoRow label="Banco"   value={cuenta.banco} />}
              {cuenta.cbu_cvu && <InfoRow label="CBU/CVU" value={cuenta.cbu_cvu} mono />}
              {cuenta.alias   && <InfoRow label="Alias"   value={cuenta.alias} mono />}
              {cuenta.titular && <InfoRow label="Titular" value={cuenta.titular} />}
              {cuenta.cuit    && <InfoRow label="CUIT"    value={cuenta.cuit} />}
            </div>
            );
          })}
        </div>
      ) : (
        <Alert type="info" style={{ marginBottom: 24 }}
          message={<>Contactate con la tienda para obtener los datos para realizar la transferencia.{total != null && <strong> Importe total: $ {Number(total).toFixed(2)}</strong>}</>}
        />
      )}

      <div style={{ border: '2px dashed #ccc', borderRadius: 8, padding: 24, marginBottom: 24, textAlign: 'center' }}>
        <Text strong style={{ display: 'block', marginBottom: 8 }}>Subí tu comprobante de pago</Text>
        <Text type="secondary" style={{ display: 'block', marginBottom: 16 }}>Imágenes (JPG, PNG) o PDF. Máximo 10 MB por archivo.</Text>
        <Button
          icon={<UploadOutlined />}
          onClick={() => inputRef.current?.click()}
          disabled={confirmando}
          style={{ borderColor: theme?.primaryColor, color: theme?.primaryColor }}
        >
          Seleccionar archivos
        </Button>
        <input
          ref={inputRef}
          type="file"
          accept="image/jpeg,image/png,image/webp,application/pdf"
          multiple
          style={{ display: 'none' }}
          onChange={handleFileChange}
        />
      </div>

      {archivos.length > 0 && (
        <div style={{ marginBottom: 24 }}>
          {archivos.map((arch, i) => (
            <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 12, padding: 12, marginBottom: 8, border: '1px solid #e0e0e0', borderRadius: 8 }}>
              {arch.mime === 'application/pdf' ? <FilePdfOutlined style={{ color: '#e53935' }} /> : <FileImageOutlined style={{ color: '#1976d2' }} />}
              <Text style={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{arch.nombre}</Text>
              {arch.estado === 'subiendo' && <Spin size="small" />}
              {arch.estado === 'ok'       && <Tag color="success">Subido</Tag>}
              {arch.estado === 'error'    && <Tag color="error">{arch.errorMsg || 'Error'}</Tag>}
            </div>
          ))}
        </div>
      )}

      <Button
        type="primary"
        block
        size="large"
        onClick={handleConfirmar}
        loading={confirmando}
        disabled={!archivos.some(a => a.estado === 'ok')}
        style={{ backgroundColor: theme?.primaryColor, borderColor: theme?.primaryColor, color: theme?.primaryTextColor }}
      >
        Confirmar envío del comprobante
      </Button>

      <Text type="secondary" style={{ display: 'block', textAlign: 'center', marginTop: 12, fontSize: 12 }}>
        Una vez confirmado, la tienda revisará el comprobante y aprobará tu pedido.
      </Text>
    </div>
  );
};

const InfoRow = ({ label, value, mono, bold, color }) => (
  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '4px 0' }}>
    <Text type="secondary" style={{ color: color || undefined }}>{label}</Text>
    <Text style={{ fontWeight: bold ? 700 : 500, fontFamily: mono ? 'monospace' : undefined, color: color || undefined, marginLeft: 16, wordBreak: 'break-all', textAlign: 'right' }}>
      {value}
    </Text>
  </div>
);
